import React, { useEffect, useMemo, useState } from "react";
import { T } from "../../app/tokens.js";
import { getCommandIndex, getCommandIndexPillars, putCommandIndexPillars } from "../../data/commandIndexClient.js";
import { emitCommandIndexStale } from "../../services/cacheBus.js";

const BUTTON = {
  border: "none",
  borderRadius: 6,
  padding: "8px 14px",
  fontSize: T.fsSm,
  fontWeight: T.fwBold,
  cursor: "pointer",
};

function scoreByKey(index) {
  const rows = Array.isArray(index?.pillars) ? index.pillars : [];
  return new Map(rows.map((row) => [row.key, row]));
}

export function PortfolioCommandPillarsPanel({ currentRole, explainer } = {}) {
  const isAdmin = currentRole === "admin";
  const [pillars, setPillars] = useState([]);
  const [scores, setScores] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getCommandIndexPillars(), getCommandIndex()])
      .then(([pillarResult, indexResult]) => {
        if (cancelled) return;
        const errors = [];
        if (pillarResult.status === "fulfilled") {
          const result = pillarResult.value;
          if (Array.isArray(result)) setPillars(result);
          else errors.push("The Portfolio Command pillars response was malformed.");
        } else {
          errors.push(pillarResult.reason?.message || "Could not load Portfolio Command pillars.");
        }
        if (indexResult.status === "fulfilled") {
          if (Array.isArray(indexResult.value?.pillars)) setScores(scoreByKey(indexResult.value));
          else errors.push("The Portfolio Command scores response was malformed.");
        } else {
          errors.push(indexResult.reason?.message || "Could not load Portfolio Command scores.");
        }
        if (errors.length) setError(errors.join(" "));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const preview = useMemo(() => {
    const scorable = pillars.map((pillar) => {
      const score = scores.get(pillar.key)?.score;
      const weight = Number(pillar.weight);
      return {
        ...pillar,
        score: typeof score === "number" && Number.isFinite(score) ? score : null,
        weight: Number.isFinite(weight) && weight >= 0 ? weight : 0,
      };
    }).filter((pillar) => pillar.status === "live" && pillar.score !== null && pillar.weight > 0);
    const totalWeight = scorable.reduce((sum, pillar) => sum + pillar.weight, 0);
    const composite = totalWeight > 0
      ? scorable.reduce((sum, pillar) => sum + pillar.score * pillar.weight, 0) / totalWeight
      : null;
    return {
      rows: scorable.map((pillar) => ({
        ...pillar,
        normalizedWeight: totalWeight > 0 ? pillar.weight / totalWeight * 100 : 0,
      })),
      composite,
    };
  }, [pillars, scores]);

  const updatePillar = (key, patch) => {
    setSaved(false);
    setPillars((current) => current.map((pillar) => pillar.key === key ? { ...pillar, ...patch } : pillar));
  };

  const handleSave = async () => {
    if (!isAdmin) return;
    setError(null);
    setSaved(false);
    const payload = pillars.map((pillar) => ({
      key: pillar.key,
      status: pillar.status,
      weight: Number(pillar.weight),
    }));
    if (!payload.length) {
      setError("No pillars to save.");
      return;
    }
    if (payload.some((pillar) => !["live", "coming_soon", "excluded"].includes(pillar.status))) {
      setError("Each pillar must be set to Live, Coming soon, or Excluded.");
      return;
    }
    if (pillars.some((pillar) => pillar.weight === "") || payload.some((pillar) => !Number.isInteger(pillar.weight) || pillar.weight < 0 || pillar.weight > 100)) {
      setError("Weights must be whole numbers from 0 to 100.");
      return;
    }
    setSaving(true);
    try {
      const result = await putCommandIndexPillars(payload);
      if (!Array.isArray(result)) throw new Error("The saved Portfolio Command pillars response was malformed.");
      setPillars(result);
      setSaved(true);
      emitCommandIndexStale();
      try {
        const current = await getCommandIndex();
        if (Array.isArray(current?.pillars)) setScores(scoreByKey(current));
        else setError("Pillars saved, but the live score preview could not be refreshed.");
      } catch {
        setError("Pillars saved, but the live score preview could not be refreshed.");
      }
    } catch (err) {
      setError(err?.message || "Could not save Portfolio Command pillars.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="portfolio-command-pillars-panel" style={{ marginBottom: 28 }}>
      <h1 style={{ margin: 0, color: T.dark, fontSize: T.fsLg5 }}>Portfolio Command pillars</h1>
      {explainer}
      {!isAdmin && (
        <div data-testid="portfolio-command-pillars-read-only" style={{ background: T.lightGray, color: T.mid, padding: "10px 12px", borderRadius: 6, marginBottom: 14 }}>
          Read-only — only admins can edit Portfolio Command pillars.
        </div>
      )}
      {error && <div data-testid="portfolio-command-pillars-error" role="alert" style={{ color: T.red, marginBottom: 12 }}>{error}</div>}
      {saved && <div data-testid="portfolio-command-pillars-saved" role="status" style={{ color: T.green, marginBottom: 12 }}>Portfolio Command pillars saved.</div>}

      {loading ? (
        <div data-testid="portfolio-command-pillars-loading" style={{ color: T.mid }}>Loading pillars…</div>
      ) : (
        <>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 7, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(150px, 1fr) 130px 130px 170px", gap: 12, padding: "9px 12px", background: T.lightGray, color: T.mid, fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase" }}>
              <span>Pillar</span><span>Status</span><span>Weight</span><span>Current score</span>
            </div>
            {pillars.map((pillar) => {
              const previewRow = preview.rows.find((row) => row.key === pillar.key);
              const rawScore = scores.get(pillar.key)?.score;
              const hasScore = typeof rawScore === "number" && Number.isFinite(rawScore);
              return (
                <div key={pillar.key} data-testid={`portfolio-command-pillar-row-${pillar.key}`} style={{ display: "grid", gridTemplateColumns: "minmax(150px, 1fr) 130px 130px 170px", gap: 12, alignItems: "center", padding: "11px 12px", borderTop: `1px solid ${T.lightGray}` }}>
                  <div style={{ color: T.dark, fontWeight: T.fwSemibold }}>{pillar.label}</div>
                  <label>
                    <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>Status for {pillar.label}</span>
                    <select
                      data-testid={`portfolio-command-pillar-status-${pillar.key}`}
                      value={pillar.status}
                      disabled={!isAdmin || saving}
                      onChange={(event) => updatePillar(pillar.key, { status: event.target.value })}
                      style={{ padding: "6px 7px", border: `1px solid ${T.border}`, borderRadius: 4, color: T.dark, background: isAdmin ? T.white : T.lightGray }}
                    >
                      <option value="live">Live</option>
                      <option value="coming_soon">Coming soon</option>
                      <option value="excluded">Excluded</option>
                    </select>
                  </label>
                  <label>
                    <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>Weight for {pillar.label}</span>
                    <input
                      data-testid={`portfolio-command-pillar-weight-${pillar.key}`}
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={pillar.weight}
                      disabled={!isAdmin || saving}
                      onChange={(event) => updatePillar(pillar.key, { weight: event.target.value })}
                      style={{ width: 88, padding: "6px 7px", border: `1px solid ${T.border}`, borderRadius: 4, color: T.dark, background: isAdmin ? T.white : T.lightGray }}
                    /> %
                  </label>
                  <div data-testid={`portfolio-command-pillar-preview-${pillar.key}`} style={{ color: T.mid, fontSize: T.fsSm }}>
                    {previewRow
                      ? `${rawScore} · ${Math.round(previewRow.normalizedWeight * 10) / 10}% normalized`
                      : hasScore
                        ? `${rawScore} · excluded from preview`
                        : "Unscored"}
                  </div>
                </div>
              );
            })}
            {pillars.length === 0 && (
              <div data-testid="portfolio-command-pillars-empty" style={{ padding: 12, color: T.mid }}>No Portfolio Command pillars are configured.</div>
            )}
          </div>
          <div data-testid="portfolio-command-pillars-preview" style={{ display: "flex", gap: 18, alignItems: "center", marginTop: 14, color: T.mid, fontSize: T.fsSm }}>
            <span>Preview: <strong style={{ color: T.dark }}>{preview.composite === null ? "Unscored" : `${Math.round(preview.composite)}`}</strong></span>
            <span>{preview.rows.length} scored live {preview.rows.length === 1 ? "pillar" : "pillars"}</span>
          </div>
          {isAdmin && (
            <button type="button" data-testid="portfolio-command-pillars-save" onClick={handleSave} disabled={saving || pillars.length === 0} style={{ ...BUTTON, background: T.blue, color: T.white, marginTop: 16 }}>
              {saving ? "Saving…" : "Save pillars"}
            </button>
          )}
        </>
      )}
    </section>
  );
}